import * as yup from "yup"

/**
 * CF7フォームの共通フロー制御（contact など複数ページで共有）
 * 入力 → 確認(readonly) → 完了(リダイレクト)。yupでバリデーション。
 * @typedef {"input" | "confirm"} FormPhase
 */

yup.setLocale({
  mixed: {
    required: "入力して下さい。",
    notType: "正しい形式で入力して下さい。",
  },
  string: {
    email: "正しい形式で入力して下さい。",
  },
})

// 日本の電話番号（ハイフン有無どちらも可）。
// funcs/contact.php のサーバー側バリデーションと同じ形にしておくこと。
const telPattern = /^0(\d{9,10}|\d{1,4}-\d{1,4}-\d{3,4})$/

/**
 * 必須のselect用スキーマを生成する
 * @returns {yup.StringSchema} 未選択（first_as_labelの空値）を弾くスキーマ
 */
const requiredSelect = (): yup.StringSchema => yup.string().required("選択して下さい。")

// --- お問い合わせ（contact）スキーマ ---
const contactSchema = yup.object().shape({
  company: yup.string().required(),
  depart: yup.string().required(),
  position: yup.string().required(),
  "your-name": yup.string().required(),
  "your-email": yup.string().required().email(),
  // 未入力時は required とメッセージが二重に出るため、空文字は形式チェックの対象外にする
  "your-tel": yup
    .string()
    .required()
    .matches(telPattern, { message: "正しい形式で入力して下さい。", excludeEmptyString: true }),
  "your-type": requiredSelect(),
  url: yup.string().url("正しい形式で入力して下さい。"),
  budget: requiredSelect(),
  motive: requiredSelect(),
  message: yup.string().required(),
  // 未チェックだとFormDataにキー自体が入らないので、required で弾ける
  consent: yup.string().required("個人情報の取り扱いに同意して下さい。"),
})

/** data-form-flow の値 → スキーマ */
const schemas: Record<string, yup.ObjectSchema<any>> = {
  contact: contactSchema,
}

/**
 * フォームの現在値を収集する（バリデーション用）
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @returns {Record<string, string>} フィールド名→値
 */
const collectValues = (form: HTMLFormElement): Record<string, string> => {
  const data = new FormData(form)
  const values: Record<string, string> = {}

  for (const [key, value] of data.entries()) {
    if (typeof value === "string") values[key] = value
  }

  return values
}

/**
 * フィールド名から、エラー表示・状態付与の対象ラッパーを取得する
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {string} name - CF7フィールド名
 * @returns {HTMLElement | null}
 */
const getFieldWrap = (form: HTMLFormElement, name: string): HTMLElement | null => {
  const wrap = form.querySelector(`.wpcf7-form-control-wrap[data-name="${name}"]`)
  return wrap?.closest<HTMLElement>(".Form__field") ?? null
}

/**
 * 全フィールドのエラー表示をクリアする
 * @param {HTMLFormElement} form - CF7フォーム要素
 */
const clearErrors = (form: HTMLFormElement): void => {
  form.querySelectorAll(".Form__field").forEach((el) => {
    el.classList.remove("hasError")
    el.querySelector(".Form__error")?.remove()
  })
}

/**
 * バリデーションエラーを各フィールドに表示する
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {yup.ValidationError} err - yupのバリデーションエラー
 * @returns {HTMLElement | null} 最初のエラー要素（スクロール先）
 */
const showErrors = (form: HTMLFormElement, err: yup.ValidationError): HTMLElement | null => {
  let firstErrorWrap: HTMLElement | null = null
  if (!err.inner) return null

  err.inner.forEach((e) => {
    if (!e.path) return
    const wrap = getFieldWrap(form, e.path)
    if (!wrap) return

    if (!firstErrorWrap) firstErrorWrap = wrap
    wrap.classList.add("hasError")

    // 1ラッパーにつきエラーは1つだけ表示
    if (!wrap.querySelector(".Form__error")) {
      const span = document.createElement("span")
      span.className = "Form__error"
      span.textContent = e.message
      wrap.appendChild(span)
    }
  })

  return firstErrorWrap
}

/**
 * CF7サーバー側のバリデーションエラーを、yupと同じスタイル（.Form__error）で各フィールドに表示する。
 * CF7の apiResponse.invalid_fields（各要素の into=対象ラッパーのセレクタ, message=メッセージ）を流用する。
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {Array<{ into?: string; message?: string }>} fields - CF7 の invalid_fields
 * @returns {HTMLElement | null} 最初のエラー要素（スクロール先）
 */
const showCf7Errors = (
  form: HTMLFormElement,
  fields: Array<{ into?: string; message?: string }>
): HTMLElement | null => {
  let firstErrorWrap: HTMLElement | null = null

  fields.forEach((f) => {
    if (!f.into) return
    const control = form.querySelector(f.into)
    const wrap = control?.closest<HTMLElement>(".Form__field")
    if (!wrap) return

    if (!firstErrorWrap) firstErrorWrap = wrap
    wrap.classList.add("hasError")

    if (!wrap.querySelector(".Form__error")) {
      const span = document.createElement("span")
      span.className = "Form__error"
      span.textContent = f.message ?? ""
      wrap.appendChild(span)
    }
  })

  return firstErrorWrap
}

/**
 * フォームの入力可否を切り替える（確認フェーズでは readonly 化）
 * @param {HTMLFormElement} form - CF7フォーム要素
 * @param {boolean} locked - true で編集不可
 */
const setFormLocked = (form: HTMLFormElement, locked: boolean): void => {
  form
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      "input[type='text'], input[type='email'], input[type='tel'], input[type='url'], textarea"
    )
    .forEach((el) => {
      if (locked) el.setAttribute("readonly", "")
      else el.removeAttribute("readonly")
    })

  // selectには readonly が無く、disabled にすると送信データから消えてしまう。
  // 値は保持したまま操作だけを封じる（マウス操作はCSSの pointer-events で止める）
  // チェックボックスも select と同じ理由（disabledは送信データから消える）で
  // 値を保持したまま操作だけを封じる
  form
    .querySelectorAll<HTMLSelectElement | HTMLInputElement>("select, input[type='checkbox']")
    .forEach((el) => {
      el.setAttribute("aria-disabled", String(locked))
      if (locked) el.setAttribute("tabindex", "-1")
      else el.removeAttribute("tabindex")
    })
}

/**
 * 電話番号入力を半角数字とハイフンだけに正規化する
 * 全角で入力されるケースが多いため、確定を待たず入力時点で寄せる
 * @param {HTMLInputElement} input - 電話番号入力欄
 */
const initTelInput = (input: HTMLInputElement): void => {
  input.addEventListener("input", () => {
    const before = input.value
    const next = before
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[ー－‐−–—]/g, "-")
      .replace(/[^\d-]/g, "")

    if (next === before) return

    const caret = input.selectionStart ?? next.length
    input.value = next
    // 値を代入するとキャレットが末尾へ飛ぶため、除去した文字数ぶん戻した位置に復帰させる
    const pos = Math.max(0, caret - (before.length - next.length))
    input.setSelectionRange(pos, pos)
  })
}

/**
 * ステッパーの表示状態を更新する（現在ステップのみ強調）
 * @param {NodeListOf<HTMLElement>} steps - .Form__stepperListItem のリスト
 * @param {string} phase - 現在のフェーズ
 */
const updateStepper = (steps: NodeListOf<HTMLElement>, phase: string): void => {
  steps.forEach((step) => {
    step.classList.toggle("is-current", step.dataset.step === phase)
  })
}

/**
 * 1つのフォームフローを初期化する
 * @param {HTMLElement} root - [data-form-flow] を持つルート要素
 */
const initFormFlow = (root: HTMLElement): void => {
  const form = root.querySelector<HTMLFormElement>("form.wpcf7-form")
  const nextBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="next"]')
  const backBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="back"]')
  const submitBtn = root.querySelector<HTMLButtonElement>('.Form__btn[data-action="submit"]')
  const realSubmit = form?.querySelector<HTMLInputElement>('input[type="submit"]')
  const steps = root.querySelectorAll<HTMLElement>(".Form__stepperListItem")
  const thanksUrl = root.dataset.thanks ?? "/"
  const schema = schemas[root.dataset.formFlow ?? ""] ?? yup.object()

  // フォームが無いページ（完了ページ等）は何もしない
  if (!form || !nextBtn || !backBtn || !submitBtn || !realSubmit) return

  // このフォームのCF7 ID（wpcf7イベントを自フォーム宛だけに絞る用）
  const cf7Id = form.querySelector<HTMLInputElement>('input[name="_wpcf7"]')?.value ?? ""

  let phase = "input"

  form.querySelectorAll<HTMLInputElement>("input[type='tel']").forEach(initTelInput)

  form.addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement
    const tag = target.tagName
    const isCheckbox = (target as HTMLInputElement).type === "checkbox"

    // Enterキーによる意図しない送信を防止（textarea以外）
    if (e.key === "Enter" && tag !== "TEXTAREA") {
      e.preventDefault()
      return
    }

    // 確認フェーズ中はキーボードからも select / チェックボックスを変更させない
    // （tabindex=-1 では、確認へ移る前からフォーカスがある場合を防げない）
    if (phase === "confirm" && (tag === "SELECT" || isCheckbox)) e.preventDefault()
  })

  /**
   * フェーズを切り替える
   * @param {string} next - 切り替え先フェーズ
   */
  const setPhase = (next: string): void => {
    phase = next
    root.classList.toggle("is-confirm", phase === "confirm")
    setFormLocked(form, phase === "confirm")
    updateStepper(steps, phase)
  }

  // 「この内容で確認をする」（入力 → 確認）
  nextBtn.addEventListener("click", async () => {
    clearErrors(form)
    const values = collectValues(form)

    try {
      await schema.validate(values, { abortEarly: false })
      setPhase("confirm")
      root.querySelector(".Form")?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch (err) {
      const first = showErrors(form, err as yup.ValidationError)
      first?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  })

  // 「入力に戻る」
  backBtn.addEventListener("click", () => {
    setPhase("input")
    root.querySelector(".Form")?.scrollIntoView({ behavior: "smooth", block: "start" })
  })

  // 「この内容で送信する」→ CF7の本来の送信をトリガー
  submitBtn.addEventListener("click", () => {
    realSubmit.click()
  })

  // 送信成功 → 完了ページへ
  document.addEventListener(
    "wpcf7mailsent",
    () => {
      window.location.href = thanksUrl
    },
    false
  )

  // サーバー側バリデーションNG → 入力に戻し、yupと同じスタイルでフィールドごとにエラー表示
  document.addEventListener(
    "wpcf7invalid",
    (e) => {
      const detail = (e as CustomEvent).detail
      // 別フォームのイベントは無視（自フォーム宛のみ）
      if (cf7Id && String(detail?.contactFormId ?? "") !== cf7Id) return

      setPhase("input")
      clearErrors(form)
      const first = showCf7Errors(form, detail?.apiResponse?.invalid_fields ?? [])
      const banner = form.querySelector<HTMLElement>(".wpcf7-response-output")
      if (banner) banner.textContent = ""
      first?.scrollIntoView({ behavior: "smooth", block: "center" })
    },
    false
  )

  // スパム判定 / メール送信失敗 → 確認のまま留めて、理由を見せてから再送信させる。
  // 入力ミスではないので入力に戻す必要がなく、戻すと「押したら入力画面に戻った」だけに見える。
  // 理由は CF7 が .wpcf7-response-output に入れる（フィールドに紐づかないエラーの唯一の出口）
  ;["wpcf7spam", "wpcf7mailfailed"].forEach((evt) => {
    document.addEventListener(
      evt,
      (e) => {
        const detail = (e as CustomEvent).detail
        // 別フォームのイベントは無視（自フォーム宛のみ）
        if (cf7Id && String(detail?.contactFormId ?? "") !== cf7Id) return

        form
          .querySelector<HTMLElement>(".wpcf7-response-output")
          ?.scrollIntoView({ behavior: "smooth", block: "center" })
      },
      false
    )
  })

  // 初期化
  setPhase("input")
}

// ページ内の全フォームフローを初期化
document.querySelectorAll<HTMLElement>("[data-form-flow]").forEach(initFormFlow)
